import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast, ToastOptions } from 'react-toastify';
import { noAutoClose } from 'assets/data/config';
import parseErrorMessage from 'utils/parseErrorMessage';

// Query keys
export const keys = {
  incidents: 'incidents',
  base: 'incident',
} as const;

// Types
interface UpdateIncidentActiveStatusParams {
  incidentId: string;
  groupGuid: string;
  active: boolean;
}

interface UpdateOperationalPeriodParams {
  incidentId: string;
  startDateTime: string;
  operationalPeriodInterval: number;
}

// Helper function for error handling
const handleError = (error: any, errorMessage: string): void => {
  const fullErrorMessage = `${errorMessage}\n\n${parseErrorMessage(error)}\n\nIf you continue to experience this error, contact support@disastertech.com`;
  console.error(fullErrorMessage);
  toast.error(fullErrorMessage, noAutoClose as ToastOptions);
};

export const useUpdateIncidentActiveStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentId,
      groupGuid,
      active,
    }: UpdateIncidentActiveStatusParams) => {
      const response = await axios(config.apiGateway.setIncidentActiveStatus, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          group_guid: groupGuid,
          active,
        },
      });
      return response.data;
    },
    onSuccess: (_, { active, incidentId }) => {
      // Invalidate both the incidents list and the specific incident data
      queryClient.invalidateQueries({ queryKey: [keys.incidents] });
      queryClient.invalidateQueries({ queryKey: [keys.base, incidentId] });
      toast.success(
        `Incident ${active ? 'activated' : 'deactivated'} successfully`
      );
    },
    onError: (error: any) => {
      handleError(error, 'setIncidentActiveStatusError');
    },
  });
};

export const useUpdateOperationalPeriod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentId,
      startDateTime,
      operationalPeriodInterval,
    }: UpdateOperationalPeriodParams) => {
      const response = await axios(config.apiGateway.upsertOperationalPeriod, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          start_date_time: startDateTime,
          operational_period_interval: operationalPeriodInterval,
        },
      });
      return response.data;
    },
    onSuccess: (_, { incidentId }) => {
      // Invalidate both the incident data and periods data queries
      queryClient.invalidateQueries({ queryKey: [keys.incidents] });
      queryClient.invalidateQueries({ queryKey: [keys.base, incidentId] });
      queryClient.invalidateQueries({
        queryKey: [keys.base, 'periods', incidentId],
      });
      toast.success('Operational period updated successfully');
    },
    onError: (error: any) => {
      handleError(error, 'Error updating operational period');
    },
  });
};
